<template>
  <!-- HERO -->
  <section class="hero jumbotron text-center text-white d-flex flex-column justify-content-center align-items-center">
    <div class="container animate__animated animate__fadeInDown">
      <img :src="logo" alt="Araucann Logo" class="hero-logo mb-4" />
      <h1 class="text-center mb-4">
        App de trazabilidad de cannabis para cultivos profesionales
      </h1>
      <p class="text-center">
        Araucann es una <strong>app de trazabilidad de cannabis</strong> que permite a clubes de cultivo, cooperativas y emprendimientos
        cumplir con normativas como <strong>GACP</strong>, <strong>GMP</strong>. Desde la semilla hasta el producto final.
      </p>
      <div class="mt-5 d-flex flex-wrap gap-2 justify-content-center">
        <router-link to="/login" class="btn btn-light d-flex align-items-center gap-2">
          <font-awesome-icon :icon="['fas', 'sign-in-alt']" />
          Ingresar
        </router-link>
        <router-link to="/register" class="btn btn-outline-light d-flex align-items-center gap-2">
          <font-awesome-icon :icon="['fas', 'user-plus']" />
          Registrarse
        </router-link>
        <button class="btn btn-warning text-dark d-flex align-items-center gap-2" @click="abrirModal = true">
          <font-awesome-icon :icon="['fas', 'star']" />
          Estoy interesado
        </button>
      </div>
    </div>
  </section>

  <!-- NUEVO: Cómo funciona -->
  <section class="py-5 bg-light border-bottom">
    <div class="container">
      <h2 class="text-center fw-bold mb-4">¿Cómo funciona Araucann paso a paso?</h2>
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <ol class="list-group list-group-numbered list-group-flush">
            <li class="list-group-item">Creamos un cultivo con fechas de inicio y fin.</li>
            <li class="list-group-item">Vinculamos un plan de tareas por etapas (Germinación, Vegetativo, Floración,
              etc.).</li>
            <li class="list-group-item">Asignamos tareas reales al personal, insumos y equipos.</li>
            <li class="list-group-item">Registramos cada paso con costos, evidencia y cumplimiento normativo.</li>
            <li class="list-group-item">Obtenemos reportes: Plan vs Real, costos.</li>
            <li class="list-group-item">Trazamos todo desde el Lote hasta el Producto Final y junto con sus etapas
              intermedias.</li>
          </ol>
          <p class="text-muted mt-3">
            Araucann te permite gestionar todo el ciclo de vida del cultivo, desde la planificación hasta la
            trazabilidad
            final, asegurando cumplimiento normativo y eficiencia operativa.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Problemas que resuelve -->
  <section class="py-5 bg-white border-bottom">
    <div class="container">
      <h2 class="text-center fw-bold mb-4">¿Qué problema resuelve Araucann?</h2>
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <ul class="list-group list-group-flush">
            <li class="list-group-item d-flex align-items-start gap-3">
              <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="text-danger mt-1" />
              <div>
                <strong>Errores por planillas sueltas y tareas sin seguimiento</strong><br />
                Unificás toda la operación en un solo lugar, sin andar con archivos sueltos.
              </div>
            </li>
            <li class="list-group-item d-flex align-items-start gap-3">
              <font-awesome-icon :icon="['fas', 'file-alt']" class="text-warning mt-1" />
              <div>
                <strong>Falta de trazabilidad clara</strong><br />
                Desde la planta hasta el producto final, todo queda guardado paso a paso.
              </div>
            </li>
            <li class="list-group-item d-flex align-items-start gap-3">
              <font-awesome-icon :icon="['fas', 'user-slash']" class="text-secondary mt-1" />
              <div>
                <strong>Dificultad para saber quién hizo qué</strong><br />
                Asignación de personal por tarea con historial, costos y evidencia.
              </div>
            </li>
            <li class="list-group-item d-flex align-items-start gap-3">
              <font-awesome-icon :icon="['fas', 'chart-line']" class="text-primary mt-1" />
              <div>
                <strong>Poca claridad sobre costos y métricas</strong><br />
                Podés ver lo que se planificó y lo que realmente pasó, con números simples y comparaciones.
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- Capturas reales -->
  <section class="py-5 bg-white border-top">
    <div class="container">
      <h2 class="text-center fw-bold mb-4">Pantallas reales del sistema</h2>
      <p class="text-center text-muted mb-5">
        Mirá cómo se ve Araucann funcionando. Estas capturas muestran ejemplos reales de la interfaz en acción.
      </p>
      <div class="row g-4">
        <div class="col-md-4" v-for="(item, i) in capturasSistema" :key="i">
          <div class="card h-100 shadow-sm border-0">
            <img :src="item.img"  class="captura-ajustada" @click="abrirImagen(item.img)"
              style="cursor: zoom-in;" alt="interfaz de app de trazabilidad de cannabis"/>
            <div class="card-body">
              <h5 class="card-title fw-bold">{{ item.titulo }}</h5>
              <p class="card-text text-muted">{{ item.descripcion }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ¿Qué podés hacer con Araucann? -->
  <section class="py-5 bg-light">
    <div class="container">
      <h2 class="text-center fw-bold mb-4">¿Qué hace Araucann por vos?</h2>
      <div class="row g-4">
        <div class="col-md-6 col-lg-4" v-for="f in funcionalidades" :key="f.titulo">
          <div class="card h-100 shadow-sm border-0">
            <div class="card-body text-center">
              <font-awesome-icon :icon="['fas', f.icono]" size="2x" class="mb-3 text-success" />
              <h5 class="card-title fw-bold">{{ f.titulo }}</h5>
              <p class="card-text text-muted">{{ f.descripcion }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>


  <!-- Módulos visuales (carrousel) -->
  <section class="bg-soft py-5">
    <div class="container">
      <h2 class="text-center fw-bold mb-5">Todo lo que tu operación necesita</h2>
      <div class="carrousel-container position-relative">
        <div v-for="(modulo, i) in modulosVisuales" :key="modulo.titulo" class="modulo-visual"
          :class="{ activo: i === indiceModulo }">
          <div class="row align-items-center flex-column flex-md-row">
            <div class="col-md-6 text-center">
              <img :src="modulo.imagen" :alt="modulo.titulo" class="img-fluid rounded shadow"
                style="max-height: 450px; object-fit: contain;" />
            </div>
            <div class="col-md-6 mt-4 mt-md-0 text-center text-md-start">
              <h3 class="fw-bold">{{ modulo.titulo }}</h3>
              <p class="fs-5 text-muted">{{ modulo.descripcion }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="text-center mt-4">
        <button class="btn btn-outline-dark me-2" @click="cambiarModulo(-1)">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
        </button>
        <button class="btn btn-outline-dark" @click="cambiarModulo(1)">
          <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>
    </div>
  </section>

  <!-- Ventajas -->
  <section class="bg-light py-5">
    <div class="container">
      <h2 class="text-center fw-bold mb-4">Beneficios concretos en el día a día</h2>
      <p class="text-center text-muted mb-5">Diseñada desde cero para cumplir con un objetivo clave: trazabilidad.</p>
      <div class="row text-center g-4">
        <div class="col-md-4 animated-box" v-for="item in beneficiosDestacados" :key="item.titulo">
          <div class="p-4 bg-white rounded shadow-sm h-100">
            <font-awesome-icon :icon="['fas', item.icono]" class="mb-3 fs-1 text-success" />
            <h5 class="fw-bold">{{ item.titulo }}</h5>
            <p class="text-muted">{{ item.descripcion }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ¿Por qué elegir Araucann? -->
  <section class="frases-impacto py-5 bg-white">
    <div class="container text-center">
      <h2 class="fw-bold mb-4">¿Por qué elegir Araucann?</h2>
      <div class="frase-card shadow mx-auto px-4 py-5">
        <div class="text-success fw-semibold mb-2 categoria">
          <font-awesome-icon :icon="['fas', frases[indice].icono]" class="me-2" />
          {{ frases[indice].categoria }}
        </div>
        <p class="frase-texto mb-0">{{ frases[indice].texto }}</p>
      </div>
      <div class="mt-4 d-flex justify-content-center gap-3">
        <button class="btn btn-outline-secondary btn-icon" @click="cambiar(-1)">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
        </button>
        <button class="btn btn-outline-secondary btn-icon" @click="cambiar(1)">
          <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>
    </div>
  </section>

  <!-- Beneficios combinados simplificados -->
  <section class="py-5 bg-white text-center">
    <h3 class="fw-bold mb-4">Beneficios combinados</h3>
    <div class="row justify-content-center g-3">
      <div class="col-6 col-md-3">
        <div class="p-3 bg-light rounded shadow-sm">
          🔗 <strong>Más trazabilidad</strong><br /><span class="text-muted small">→ más valor</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="p-3 bg-light rounded shadow-sm">
          💼 <strong>Más orden</strong><br /><span class="text-muted small">→ más cumplimiento</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="p-3 bg-light rounded shadow-sm">
          📊 <strong>Más datos</strong><br /><span class="text-muted small">→ mejores decisiones</span>
        </div>
      </div>
    </div>
  </section>

  <!-- Precio y CTA -->
  <section class="py-5 bg-white border-top text-center">
    <div class="container">
      <h2 class="fw-bold mb-4">Accedé a Araucann</h2>
      <p class="fs-5 text-muted mb-4">
        Si tu cultivo necesita más orden, trazabilidad y cumplimiento, probá Araucann.
      </p>
      <div class="precio-box d-inline-block px-4 py-3 rounded shadow-sm mb-4">
        <div class="display-5 fw-bold text-success">
          $15.000 <span class="fs-6 text-muted">ARS / mes</span>
        </div>
        <div class="text-muted small mt-1">
          Incluye todo: trazabilidad, soporte, legalidad, sin letra chica.
        </div>
      </div>
      <p class="text-muted">
        <font-awesome-icon :icon="['fas', 'leaf']" class="text-success me-1" />
        ¿Sos PyME o club de cultivo? Hablamos y vemos cómo te adaptás.
      </p>

    </div>
  </section>

  <!-- CTA emocional final -->
  <section class="bg-success text-white text-center py-4">
    <h4 class="fw-bold mb-2">¿Te sirve? Pedime acceso y lo charlamos.</h4>
    <button class="btn btn-light" @click="abrirModal = true">Solicitar acceso</button>
  </section>

  <!-- Contacto -->
  <section class="bg-dark text-white py-5">
    <div class="container text-center">
      <h2 class="fw-bold mb-4">¿Querés contactarnos directamente?</h2>
      <p class="lead mb-4">Si tenés dudas o querés saber más, escribime por email o Instagram.</p>
      <div class="row justify-content-center g-4">
        <div class="col-md-5">
          <div class="p-4 bg-white text-dark rounded shadow-sm h-100">
            <h5 class="fw-bold">Correo electrónico</h5>
            <p class="mb-1">Escribinos a:</p>
            <a href="mailto:araucannsoftware@gmail.com" class="text-decoration-none fw-semibold">
              araucannsoftware@gmail.com
            </a>
          </div>
        </div>
        <div class="col-md-5">
          <div class="p-4 bg-white text-dark rounded shadow-sm h-100">
            <h5 class="fw-bold">Instagram</h5>
            <p class="mb-1">Seguinos o mandanos un mensaje:</p>
            <a href="https://www.instagram.com/araucann.software" target="_blank"
              class="text-decoration-none fw-semibold">
              @araucann.software
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Modales -->
  <transition name="fade">
    <div v-if="abrirModal" class="modal-mask">
      <div class="modal-wrapper">
        <button type="button" class="btn-close float-end" @click="abrirModal = false" aria-label="Cerrar"></button>
        <SolicitudUso @cancelar="cancelarSolicitud" />
      </div>
    </div>
  </transition>

  <transition name="fade">
    <div v-if="imagenAmpliada" class="lightbox-mask" @click.self="cerrarImagen">
      <div class="lightbox-wrapper">
        <img :src="imagenAmpliada" class="img-fluid rounded shadow" />
        <button class="btn btn-sm btn-light mt-3" @click="cerrarImagen">Cerrar</button>
      </div>
    </div>
  </transition>
</template>



<script>
import SolicitudUso from '@/components/SolicitudUso/SolicitudUso.vue';

import logo from '@/assets/logo.webp';

// Imágenes de módulos visuales
import imgCultivo from '@/assets/inicio/1_cultivo.jpg'
import imgPlanificacion from '@/assets/inicio/2_planificacion.jpg'
import imgTrazabilidad from '@/assets/inicio/3_trazabilidad.jpg'
import imgPersonal from '@/assets/inicio/4_personal.jpg'
import imgCumplimiento from '@/assets/inicio/5_cumplimiento.jpg'
import imgInventario from '@/assets/inicio/6_inventario.jpg'
import imgCostos from '@/assets/inicio/7_costos.jpg'

// Capturas reales del sistema
import imgPlanVsReal from '@/assets/inicio/capturasAraucann/planvsreal.jpg'
import imgGrafo from '@/assets/inicio/capturasAraucann/grafoTrazabilidad.jpg'
import imgVerEvento from '@/assets/inicio/capturasAraucann/verevento.jpg'

export default {
  name: 'BienvenidaPublica',
  components: {
    SolicitudUso
  },
  data() {
    return {
      abrirModal: false,
      logo,
      indiceModulo: 0,
      intervaloAuto: null,
      imagenAmpliada: null,
      indice: 0,
      intervalo: null,

      modulosVisuales: [
        { titulo: 'Trazabilidad', imagen: imgTrazabilidad, descripcion: 'Desde semilla hasta el producto final. Conectá todos los procesos en línea.' },
        { titulo: 'Cultivo y Gestión', imagen: imgCultivo, descripcion: 'Administrá tus salas, plantas, lotes y genética con trazabilidad total.' },
        { titulo: 'Planificación', imagen: imgPlanificacion, descripcion: 'Diseñá planes de cultivo, actividades por etapa y tareas por día.' },
        { titulo: 'Gestión de Personal', imagen: imgPersonal, descripcion: 'Asigná tareas, medí el desempeño y visualizá carga de trabajo.' },
        { titulo: 'Cumplimiento Normativo', imagen: imgCumplimiento, descripcion: 'Registrá evidencia de cumplimiento normativo, de procedimientos y buenas prácticas.' },
        { titulo: 'Inventario', imagen: imgInventario, descripcion: 'Controlá insumos, stock mínimo, consumo por tareas y proveedores.' },
        { titulo: 'Costos Operativos', imagen: imgCostos, descripcion: 'Visualizá los costos reales por fase, lote, planta o producto final.' },
      ],

      capturasSistema: [
        {
          titulo: 'Trazabilidad Visual',
          img: imgGrafo,
          descripcion: 'Seguimiento completo desde el lote hasta el producto final. Cada paso queda registrado.'
        },
        {
          titulo: 'Plan vs Real',
          img: imgPlanVsReal,
          descripcion: 'Comparación de tareas planificadas y ejecutadas, con cálculo de desvíos y costos reales.'
        },
        {
          titulo: 'Etapa de Floración',
          img: imgVerEvento,
          descripcion: 'Visualización de tareas, tiempo y costos por etapa de cultivo como Floración o Germinación.'
        }
      ],

      funcionalidades: [
        { titulo: 'Trazabilidad completa', icono: 'route', descripcion: 'Conectá todas las etapas: desde la semilla hasta el producto final.' },
        { titulo: 'Planificación simple', icono: 'calendar-check', descripcion: 'Organizá tareas, etapas y recursos en un solo lugar.' },
        { titulo: 'Cumplimiento sin estrés', icono: 'shield-alt', descripcion: 'Tené todo listo para ARICCAME, GACP o inspecciones.' },
        { titulo: 'Costos reales y claros', icono: 'file-invoice-dollar', descripcion: 'Visualizá cuánto cuesta cada parte de tu operación.' },
        { titulo: 'Equipo organizado', icono: 'users', descripcion: 'Asigná tareas, revisá avances y evitá confusiones.' },
        { titulo: 'Todo en un solo lugar', icono: 'cubes', descripcion: 'Dejá atrás las planillas sueltas y el caos de mil herramientas.' }

      ],

      beneficiosDestacados: [
        { icono: 'route', titulo: 'Trazabilidad completa', descripcion: 'Cada planta, tarea, lote o producto está conectado y registrado paso a paso.' },
        { icono: 'shield-alt', titulo: 'Preparado para controles', descripcion: 'Si te inspeccionan, tenés todo en orden. Mostrá trazabilidad y evidencia con unos pocos clicks.' },
        { icono: 'calendar-check', titulo: 'Planificación diaria', descripcion: 'Planes reutilizables con tareas por etapa.' },
        { icono: 'file-invoice-dollar', titulo: 'Costos bajo control', descripcion: 'Cada tarea o insumo tiene costo estimado y real.' },
        { icono: 'chart-line', titulo: 'Decisiones con datos', descripcion: 'Plan vs Real, indicadores y métricas de rendimiento.' },
        { icono: 'user-shield', titulo: 'Cumplimiento legal', descripcion: 'Adaptado a GACP, GMP y normativa ARICCAME.' },
        { icono: 'cloud', titulo: '100% online', descripcion: 'Accedé desde cualquier lugar, sin instalaciones.' }
      ],

      frases: [
        { categoria: 'Trazabilidad', icono: 'route', texto: 'Cada planta, cada paso, cada producto: todo trazado.' },
        { categoria: 'Cumplimiento', icono: 'shield-alt', texto: 'Cumplimiento normativo claro, documentado y trazado.' },
        { categoria: 'Planificación', icono: 'calendar-check', texto: 'Diseñá y ejecutá tareas con visión y orden.' },
        { categoria: 'Costos', icono: 'file-invoice-dollar', texto: 'Cada tarea tiene su costo. Cada insumo, su trazabilidad.' },
        { categoria: 'Datos', icono: 'chart-line', texto: 'Decisiones inteligentes con datos precisos.' }
      ]
    };
  },
  watch: {
    abrirModal(val) {
      document.body.style.overflow = val ? 'hidden' : '';
    }
  },
  mounted() {
    this.iniciarRotacionAutomatica();
    this.intervalo = setInterval(() => this.cambiar(1), 6000);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate__animated', 'animate__fadeInUp');
          } else {
            entry.target.classList.remove('animate__fadeInUp', 'animate__animated');
          }
        });
      },
      { threshold: 0.4 }
    );
    document.querySelectorAll('.animated-box').forEach(el => observer.observe(el));
  },
  beforeUnmount() {
    clearInterval(this.intervaloAuto);
    clearInterval(this.intervalo);
  },
  methods: {
    cambiarModulo(direccion) {
      const total = this.modulosVisuales.length;
      this.indiceModulo = (this.indiceModulo + direccion + total) % total;
    },
    iniciarRotacionAutomatica() {
      this.intervaloAuto = setInterval(() => this.cambiarModulo(1), 4500);
    },
    cambiar(direccion) {
      const total = this.frases.length;
      this.indice = (this.indice + direccion + total) % total;
    },
    abrirImagen(img) {
      this.imagenAmpliada = img;
    },
    cerrarImagen() {
      this.imagenAmpliada = null;
    },
    cancelarSolicitud() {
      this.abrirModal = false;
    }
  }
}
</script>


<style scoped>
.hero {
  padding: 80px 20px;
  background: radial-gradient(circle at 80% 30%, rgba(255, 255, 255, 0.03) 0%, transparent 70%),
    radial-gradient(circle at 20% 70%, rgba(255, 255, 255, 0.04) 0%, transparent 60%),
    linear-gradient(135deg, #4a7d3a, #355e2a);
  color: white;
  position: relative;
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 95%, 0 100%);
}

.hero-logo {
  max-width: 170px;
  height: auto;
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;
}

.hero-logo:hover {
  transform: scale(1.05);
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(45deg,
      rgba(255, 255, 255, 0.015) 0,
      rgba(255, 255, 255, 0.015) 1px,
      transparent 1px,
      transparent 16px);
  z-index: 0;
  pointer-events: none;
}

.hero::after {
  content: 'Araucann';
  position: absolute;
  font-size: 160px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.03);
  bottom: -10px;
  right: 8%;
  z-index: 0;
  white-space: nowrap;
  pointer-events: none;
}

.hero .container {
  position: relative;
  z-index: 1;
  max-width: 800px;
  text-align: center;
}

.hero p {
  max-width: 600px;
  margin: 0 auto;
  color: #dce5dc;
}

.bg-soft {
  background-color: #f9f8f6;
}

/* BENEFICIOS DESTACADOS */
.animated-box {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.animated-box.animate__animated {
  opacity: 1;
}

.carousel-container {
  position: relative;
  min-height: 350px;
}



/* MODAL */
.modal-mask {
  position: fixed;
  z-index: 9999;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 40px 20px;
}

.modal-wrapper {
  background: #ffffff;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 700px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: fadeInUp 0.4s ease;
  z-index: 10000;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* FORMULARIO SOLICITUD */
.solicitud-form label {
  color: #333;
  font-weight: 500;
}

.solicitud-form input,
.solicitud-form textarea,
.solicitud-form select {
  background-color: #fff;
  color: #212529;
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  margin-bottom: 15px;
}

.solicitud-form h2 {
  font-size: 1.6rem;
  font-weight: bold;
  color: #264653;
}

/* FADE GENERAL */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .hero::after {
    display: none;
  }

  .hero h1 {
    font-size: 2.4rem;
  }
}

.carrousel-container {
  min-height: 480px;
  position: relative;
}

.modulo-visual {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  opacity: 0;
  z-index: 0;
  transition: opacity 0.6s ease;
  pointer-events: none;
  /* 🔑 clave para que los ocultos no interfieran */

}

.modulo-visual.activo {
  opacity: 1;
  z-index: 1;
  position: relative;
  pointer-events: auto;
  /* 🔓 sólo el activo responde a eventos */

}


.bg-dark a:hover {
  color: #ffc107 !important;
  /* amarillo suave al hacer hover */
}

.bg-white a {
  color: #2c7a7b;
}

.bg-white a:hover {
  color: #205e5f;
  text-decoration: underline;
}

.frase-card {
  background: #fff;
  border-radius: 14px;
  max-width: 600px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
}

.categoria {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.frase-texto {
  font-size: 1.25rem;
  font-weight: 500;
  color: #212529;
}

.btn-icon {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.card-img-top {
  object-fit: cover;
  height: 250px;
}

.card:hover {
  transform: scale(1.02);
  transition: transform 0.3s ease;
}

.captura-ajustada {
  width: 100%;
  height: 220px;
  object-fit: contain;
  object-position: top;
  background-color: #f8f9fa;
  padding: 10px;
  border-bottom: 1px solid #dee2e6;
}

.lightbox-mask {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.lightbox-wrapper {
  max-width: 90%;
  max-height: 90%;
  text-align: center;
}

.lightbox-wrapper img {
  max-height: 80vh;
  max-width: 100%;
  border: 4px solid white;
  background: white;
}

.precio-box {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}
</style>
